import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import Icon from "./Icons";

const navItems = [
  { path: "/addbook", label: "Today", icon: "home" },
  { path: "/library", label: "Library", icon: "library" },
  { path: "/journal", label: "Journal", icon: "journal" },
  { path: "/collections", label: "Collections", icon: "collections" },
];

function Brand() {
  return (
    <Link to="/" className="group flex items-center gap-2.5 text-cream no-underline">
      <span className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-xl bg-gold text-ink-base shadow-gold-sm transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-105">
        <span className="font-serif text-xl font-bold leading-none">S</span>
      </span>
      <span className="font-serif text-lg font-semibold tracking-[-0.02em]">StoryChats</span>
    </Link>
  );
}

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isLoggedIn, setIsLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    ["userId", "encryptedUserId", "authToken", "firstName", "lastName"].forEach(
      key => localStorage.removeItem(key)
    );
    setIsLoggedIn(false);
    navigate("/");
  };

  const isActive = path => location.pathname === path;

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-ink-base/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Brand />

          {isLoggedIn ? (
            <>
              <nav className="hidden items-center rounded-2xl border border-ink-border bg-ink-surface/70 p-1 md:flex">
                {navItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-medium no-underline transition-all duration-200 ${
                      isActive(item.path)
                        ? "bg-ink-raised text-cream shadow-card"
                        : "text-cream-muted hover:text-cream"
                    }`}
                  >
                    <Icon name={item.icon} size={15} />
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="hidden items-center gap-2 md:flex">
                <Link to="/profile" aria-label="Profile" className={`sc-icon-button no-underline ${isActive("/profile") ? "border-gold/50 text-gold" : ""}`}>
                  <Icon name="user" size={17} />
                </Link>
                <button onClick={handleLogout} className="px-2 py-2 text-xs font-medium text-cream-muted transition-colors hover:text-cream">
                  Sign out
                </button>
              </div>

              <button
                aria-expanded={menuOpen}
                aria-label="Open menu"
                className="sc-icon-button md:hidden"
                onClick={() => setMenuOpen(open => !open)}
              >
                <Icon name={menuOpen ? "close" : "menu"} size={20} />
              </button>
            </>
          ) : (
            <nav className="flex items-center gap-2">
              <Link to="/login" className="hidden px-4 py-2 text-sm font-medium text-cream-muted no-underline transition-colors hover:text-cream sm:block">Sign in</Link>
              <Link to="/register" className="sc-btn-gold px-4 py-2 text-xs no-underline">
                Start reading <Icon name="arrow" size={14} />
              </Link>
            </nav>
          )}
        </div>

        {menuOpen && isLoggedIn && (
          <div className="border-t border-ink-border bg-ink-surface/95 px-5 py-4 backdrop-blur-xl md:hidden">
            <div className="grid grid-cols-2 gap-2">
              {[...navItems, { path: "/profile", label: "Profile", icon: "user" }].map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-xl p-3 text-sm no-underline transition-colors ${isActive(item.path) ? "bg-ink-raised text-gold" : "text-cream-dim"}`}
                >
                  <Icon name={item.icon} size={17} /> {item.label}
                </Link>
              ))}
              <button onClick={handleLogout} className="rounded-xl p-3 text-left text-sm text-cream-muted">Sign out</button>
            </div>
          </div>
        )}
      </header>

      {isLoggedIn && (
        <nav className="fixed bottom-3 left-1/2 z-40 flex w-[calc(100%-24px)] max-w-sm -translate-x-1/2 items-center justify-around rounded-2xl border border-ink-border bg-ink-surface/90 p-1.5 shadow-lift backdrop-blur-xl md:hidden">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              aria-label={item.label}
              className={`flex min-w-[58px] flex-col items-center gap-1 rounded-xl px-2 py-2 text-[9px] font-medium no-underline transition-all ${isActive(item.path) ? "bg-ink-raised text-gold" : "text-cream-muted"}`}
            >
              <Icon name={item.icon} size={18} />
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </>
  );
};

export default Header;
